import React, { useEffect, useState } from "react";
import { ThemeProvider } from "@material-ui/core";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import queryString from "query-string";
import { persistQueryClient } from "react-query/persistQueryClient-experimental";
import { createWebStoragePersistor } from "react-query/createWebStoragePersistor-experimental";

import createMyTheme from "./AppTheme";

import {
  generarCodigo2FA,
  getRepresentadosPorApp,
  login,
  logout,
  setSelectedServicio,
  hideMissingDataPopup,
  setNotificationToken,
} from "../../store/actions";
import { useMessageHandler } from "./hooks/use-message-handler";

import MyAppbar from "../Appbar/Appbar";
import PrivateRoute from "./PrivateRoute/PrivateRoute";
import Landing from "./Landing/Landing";
import IsMobile from "../UI/IsMobile/IsMobile";
import Loading from "../UI/Loading/Loading";
import { REFRESH_TOKEN, TOKEN } from "../../constants";
import { ServicioType } from "../../models/Types/ServicioType";
import { cookieValue } from "../../utils/helper";
import { SinPermiso } from "../../pages/SinPermiso";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import MissingDataPopup from "../UI/MissingDataPopup/MissingDataPopup";

// document.cookie = `tokenVeDi=; path=/`;
// document.cookie = `refreshTokenVeDi=; path=/`;

const theme = createMyTheme();

const mapStateToProps = (state: any) => ({
  isAuth: state.usuario.isAuth,
  isAuthLoading: state.usuario.isLoading,
  isLoading: state.loading.isLoading,
  usuario: state.usuario.user,
  showMissingDataPopup: state.usuario.showMissingDataPopup,
  notificationToken: state.usuario.notificationToken,
});

const mapDispatchToProps = (dispatch: any) => ({
  login: (token: string, refreshToken: string) =>
    dispatch(login(token, refreshToken)),
  logout: () => dispatch(logout()),
  getRepresentadosPorApp: (idAplicacion: number) =>
    dispatch(getRepresentadosPorApp(idAplicacion)),
  setSelectedServicio: (servicio: ServicioType) =>
    dispatch(setSelectedServicio(servicio)),
  generarCodigo2FA: (token: string) => dispatch(generarCodigo2FA(token)),
  hideMissingDataPopup: () => dispatch(hideMissingDataPopup()),
  setNotificationToken: (token: string) =>
    dispatch(setNotificationToken(token)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 1 * 15,
    },
  },
});

const localStoragePersistor = createWebStoragePersistor({
  storage: window.localStorage,
});

persistQueryClient({
  queryClient,
  persistor: localStoragePersistor,
});

const App: React.FC<Props> = (props) => {
  const {
    isLoading,
    usuario,
    login,
    logout,
    getRepresentadosPorApp,
    setSelectedServicio,
    isAuth,
    isAuthLoading,
    showMissingDataPopup,
    hideMissingDataPopup,
    setNotificationToken,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const [_loading, setLoading] = useState(isLoading ?? false);

  useMessageHandler(usuario);
  useEffect(() => {
    const query = queryString.parse(location.search);
    const tokenFromQuery = query.tokenVeDi as string;
    const refreshFromQuery = query.refreshTokenVeDi as string;
    const notificationTokenFromQuery = query.notificationToken as string;
    const idAplicacion = localStorage.getItem("idAplicacion");

    if (tokenFromQuery && refreshFromQuery) {
      document.cookie = `tokenVeDi=${tokenFromQuery}; path=/`;
      document.cookie = `refreshTokenVeDi=${refreshFromQuery}; path=/`;
    }

    if (notificationTokenFromQuery)
      setNotificationToken(notificationTokenFromQuery);

    const token = cookieValue(TOKEN);
    const refreshToken = cookieValue(REFRESH_TOKEN);

    if (!token) {
      try {
        logout();
      } catch (error) {
        console.error("error  al cerrar sesión:", error);
      } finally {
        return;
      }
    }

    try {
      login(token, refreshToken);

      if (idAplicacion) {
        setLoading(true);
        localStorage.removeItem("idAplicacion");
        getRepresentadosPorApp(parseInt(idAplicacion))
          .then((app: any) => {
            if (app) {
              if (app.representados?.length > 0) {
                setSelectedServicio(app);
                navigate(`servicios/representados`);
                setLoading(false);
                return;
              } else {
                const { urlBase } = app;
                if (!app.impedirAcceso) {
                  window.open(urlBase, "_self");
                  return;
                }
              }
            }
            setLoading(false);
          })
          .catch((error: unknown) => console.error("Error", error));
      }
      if (query.redirectTo) {
        navigate("/servicios?redirectTo=" + query.redirectTo);
      }
    } catch (error) {
      console.error("error de login", error);
    }
  }, [
    getRepresentadosPorApp,
    navigate,
    location.search,
    login,
    logout,
    setSelectedServicio,
  ]);

  useEffect(() => {
    const version = process.env.REACT_APP_VERSION;
    document.cookie = `app_version=${version}; path=/`;
  }, []);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const handleGoToProfile = () => {
    hideMissingDataPopup();
    navigate("/mi-cuenta");
  };

  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <Loading loading={_loading || isAuthLoading} />
        {!isAuthLoading && (
          <Routes>
            <Route path="/landing" element={<Landing />} />
            <Route path="/sin-permisos" element={<SinPermiso />} />
            <Route
              path="/*"
              element={
                <IsMobile>
                  <PrivateRoute isAuthenticated={isAuth}>
                    <MyAppbar />
                  </PrivateRoute>
                </IsMobile>
              }
            />
          </Routes>
        )}
        <MissingDataPopup
          open={showMissingDataPopup}
          onClose={hideMissingDataPopup}
          onGoToProfile={handleGoToProfile}
          userName={usuario?.nombre}
        />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default connector(App);
